// Computes a time-series trend: aggregates a measure over a date column,
// bucketed by period (year/quarter/month/day), and summarizes direction/growth.

function toPeriodKey(rawValue, period) {

  // If it's already just a bare year number (e.g. 2013), use it directly
  // for yearly trends without trying to parse it as a Date.
  if (period === "year" && /^\d{4}$/.test(String(rawValue).trim())) {
    return String(rawValue).trim();
  }

  const date = new Date(rawValue);

  if (isNaN(date.getTime())) {
    return String(rawValue); // fall back to raw value if unparseable
  }

  const year = date.getFullYear();
  const month = date.getMonth() + 1; // 1-12

  switch (period) {

    case "year":
      return String(year);

    case "quarter": {
      const q = Math.ceil(month / 3);
      return `${year}-Q${q}`;
    }

    case "month":
      return `${year}-${String(month).padStart(2, "0")}`;

    case "day":
      return date.toISOString().slice(0, 10);

    default:
      return String(year);
  }
}

export function computeTrend(data, { dateColumn, measure, period = "year", aggregation = "sum" } = {}) {

  if (!Array.isArray(data) || !data.length) {
    return { success: false, error: "No data available for trend." };
  }

  if (!dateColumn) {
    return { success: false, error: "trend requires a date/time column." };
  }

  const buckets = {};

  for (const row of data) {

    const key = toPeriodKey(row[dateColumn], period);

    if (!buckets[key]) buckets[key] = [];

    buckets[key].push(row);
  }

  const series = Object.keys(buckets)
    .sort() // period keys are formatted to sort chronologically as strings
    .map(key => {

      const rows = buckets[key];

      if (aggregation === "count") {
        return { period: key, value: rows.length };
      }

      const values = rows
        .map(r => Number(r[measure]))
        .filter(v => !isNaN(v));

      let value = null;

      if (values.length) {
        switch (aggregation) {
          case "average":
            value = values.reduce((a, b) => a + b, 0) / values.length;
            break;
          case "maximum":
            value = Math.max(...values);
            break;
          case "minimum":
            value = Math.min(...values);
            break;
          default:
            value = values.reduce((a, b) => a + b, 0);
        }
      }

      return { period: key, value };
    });

  if (series.length < 2) {

    return {
      success: true,
      tool: "trend",
      measure,
      period,
      aggregation,
      series,
      direction: "insufficient-data",
    };
  }

  const first = series[0].value;
  const last = series[series.length - 1].value;

  const change = (first !== null && last !== null) ? last - first : null;

  const pctChange = (change !== null && first)
    ? (change / Math.abs(first)) * 100
    : null;

  const direction =
    change === null ? "unknown" :
    change > 0 ? "increasing" :
    change < 0 ? "decreasing" : "flat";

  return {
    success: true,
    tool: "trend",
    measure,
    period,
    aggregation,
    series,
    first,
    last,
    change,
    pctChange,
    direction,
  };
}
